import React, { useEffect, useState } from "react";
import api, { formatIDR, formatDate, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, ArrowsLeftRight, ArrowDown, ArrowUp, Bank, Wallet, MagnifyingGlass, PencilSimple, FilePdf, FileXls, DownloadSimple, Upload } from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePaginated } from "@/components/Paginator";
import ImportProgressDialog from "@/components/ImportProgressDialog";
import { DateRangeFilter, firstOfMonth, todayStr, buildDateQuery } from "@/components/DateRangeFilter";
import { ProjectCombobox } from "@/components/ProjectCombobox";

export default function CashBank() {
  const [tab, setTab] = useState("accounts");
  const [accounts, setAccounts] = useState([]);
  const [txns, setTxns] = useState([]);
  const [partners, setPartners] = useState([]);
  const [coa, setCoa] = useState([]);
  const [accOpen, setAccOpen] = useState(false);
  const [rcpOpen, setRcpOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [tfOpen, setTfOpen] = useState(false);
  const today = () => new Date().toISOString().slice(0, 10);
  const [search, setSearch] = useState("");

  const [accForm, setAccForm] = useState({ name: "", type: "bank", account_code: "1100", bank_name: "", account_number: "", account_holder: "", bank_branch: "", opening_balance: 0, currency: "IDR" });
  const [projects, setProjects] = useState([]);
  const [rcpForm, setRcpForm] = useState({ date: today(), cash_bank_id: "", counter_account_code: "4000", partner_id: "", partner_name: "", project_id: "", project_name: "", amount: 0, description: "", reference: "" });
  const [payForm, setPayForm] = useState({ date: today(), cash_bank_id: "", counter_account_code: "6000", partner_id: "", partner_name: "", project_id: "", project_name: "", amount: 0, description: "", reference: "" });
  const [tfForm, setTfForm] = useState({ date: today(), from_cash_bank_id: "", to_cash_bank_id: "", amount: 0, admin_fee: 0, admin_fee_account_code: "6500", project_id: "", project_name: "", description: "", reference: "" });
  const [editAcc, setEditAcc] = useState(null);
  const [detailAcc, setDetailAcc] = useState(null);
  const [mutations, setMutations] = useState(null);

  const [dateRange, setDateRange] = useState({ from: firstOfMonth(), to: todayStr() });
  const load = () => {
    api.get("/cashbank/accounts").then((r) => setAccounts(r.data));
    api.get(`/cashbank/transactions${buildDateQuery(dateRange)}`).then((r) => setTxns(r.data));
    api.get("/partners").then((r) => setPartners(r.data));
    api.get("/accounting/accounts").then((r) => setCoa(r.data));
    api.get("/projects?active_only=true").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  };
  useEffect(() => { load(); }, [dateRange]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterQ = (arr, fields) => {
    const q = search.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter(r => fields.some(f => (r[f] || "").toString().toLowerCase().includes(q)));
  };
  const filteredAccounts = filterQ(accounts, ["name", "bank_name", "account_number", "account_code"]);
  const filteredTxns = filterQ(txns, ["description", "reference", "cash_bank_name", "counter_account_name", "partner_name"]);
  const pgTxns = usePaginated(filteredTxns);

  const saveAcc = async () => {
    try {
      if (editAcc) await api.put(`/cashbank/accounts/${editAcc.id}`, { ...accForm, opening_balance: Number(accForm.opening_balance) });
      else await api.post("/cashbank/accounts", { ...accForm, opening_balance: Number(accForm.opening_balance) });
      toast.success(editAcc ? "Akun & mapping COA diperbarui" : "Akun kas/bank tersimpan"); setAccOpen(false); setEditAcc(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const openNewAcc = () => { setEditAcc(null); setAccForm({ name: "", type: "bank", account_code: "1100", bank_name: "", account_number: "", account_holder: "", bank_branch: "", opening_balance: 0, currency: "IDR" }); };
  const openEditAcc = (a) => {
    setEditAcc(a);
    setAccForm({ name: a.name, type: a.type, account_code: a.account_code, bank_name: a.bank_name || "", account_number: a.account_number || "", account_holder: a.account_holder || "", bank_branch: a.bank_branch || "", opening_balance: a.opening_balance || 0, currency: a.currency || "IDR" });
    setAccOpen(true);
  };
  const delAcc = async (a) => {
    if (!window.confirm(`Hapus akun ${a.name}? Hanya bisa jika belum ada mutasi transaksi.`)) return;
    try { await api.delete(`/cashbank/accounts/${a.id}`); toast.success(`Akun ${a.name} dihapus`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };
  const [mutRange, setMutRange] = useState({ from: firstOfMonth(), to: todayStr() });
  const mutQs = (r) => `date_from=${r.from || ""}&date_to=${r.to || ""}`;
  const loadMutations = async (a, r) => {
    setMutations(null);
    try { const res = await api.get(`/cashbank/accounts/${a.id}/mutations?${mutQs(r)}`); setMutations(res.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat mutasi"); setDetailAcc(null); }
  };
  const openDetail = (a) => {
    const r = { from: firstOfMonth(), to: todayStr() };
    setDetailAcc(a); setMutRange(r); loadMutations(a, r);
  };
  const changeMutRange = (r) => { setMutRange(r); if (detailAcc) loadMutations(detailAcc, r); };
  const exportMut = async (fmt) => {
    if (!detailAcc) return;
    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/cashbank/accounts/${detailAcc.id}/mutations/export?fmt=${fmt}&${mutQs(mutRange)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error((await resp.json().catch(() => ({}))).detail || "Gagal membuat dokumen");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `mutasi_${detailAcc.name.replace(/\s+/g, "_")}.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Mutasi ${detailAcc.name} diunduh (${fmt.toUpperCase()})`);
    } catch (e) { toast.error(e.message); }
  };

  const [editRcp, setEditRcp] = useState(null);
  const [editPay, setEditPay] = useState(null);
  const [editTf, setEditTf] = useState(null);

  const saveRcp = async () => {
    try {
      const body = { ...rcpForm, amount: Number(rcpForm.amount) };
      if (editRcp) await api.put(`/cashbank/transactions/${editRcp.id}`, body);
      else await api.post("/cashbank/receipt", body);
      toast.success(editRcp ? "Penerimaan diperbarui + jurnal diubah" : "Penerimaan tersimpan + jurnal dibuat");
      setRcpOpen(false); setEditRcp(null);
      setRcpForm({ date: today(), cash_bank_id: "", counter_account_code: "4000", partner_id: "", partner_name: "", project_id: "", project_name: "", amount: 0, description: "", reference: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const savePay = async () => {
    try {
      const body = { ...payForm, amount: Number(payForm.amount) };
      if (editPay) await api.put(`/cashbank/transactions/${editPay.id}`, body);
      else await api.post("/cashbank/payment", body);
      toast.success(editPay ? "Pengeluaran diperbarui + jurnal diubah" : "Pengeluaran tersimpan + jurnal dibuat");
      setPayOpen(false); setEditPay(null);
      setPayForm({ date: today(), cash_bank_id: "", counter_account_code: "6000", partner_id: "", partner_name: "", project_id: "", project_name: "", amount: 0, description: "", reference: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const saveTf = async () => {
    try {
      const body = { ...tfForm, amount: Number(tfForm.amount), admin_fee: Number(tfForm.admin_fee) };
      if (editTf) await api.put(`/cashbank/transactions/${editTf.id}`, body);
      else await api.post("/cashbank/transfer", body);
      toast.success(editTf ? "Transfer diperbarui + jurnal diubah" : "Transfer tersimpan");
      setTfOpen(false); setEditTf(null);
      setTfForm({ date: today(), from_cash_bank_id: "", to_cash_bank_id: "", amount: 0, admin_fee: 0, admin_fee_account_code: "6500", project_id: "", project_name: "", description: "", reference: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const openEditTxn = async (t) => {
    // Ambil pasangan transaksi (untuk transfer) jika perlu — pakai field cash_bank_id + counter_account_code dari row
    if (t.type === "receipt") {
      setEditRcp(t);
      setRcpForm({
        date: t.date, cash_bank_id: t.cash_bank_id, counter_account_code: t.counter_account_code,
        partner_id: t.partner_id || "", partner_name: t.partner_name || "",
        amount: t.amount, description: t.description || "", reference: t.reference || "",
      });
      setRcpOpen(true);
    } else if (t.type === "payment") {
      setEditPay(t);
      setPayForm({
        date: t.date, cash_bank_id: t.cash_bank_id, counter_account_code: t.counter_account_code,
        partner_id: t.partner_id || "", partner_name: t.partner_name || "",
        amount: t.amount, description: t.description || "", reference: t.reference || "",
      });
      setPayOpen(true);
    } else if (t.type === "transfer_in" || t.type === "transfer_out") {
      // Butuh pasangan untuk tahu from/to
      const sibling = txns.find(x => x.journal_entry_id === t.journal_entry_id && x.id !== t.id);
      const outRow = t.type === "transfer_out" ? t : sibling;
      const inRow = t.type === "transfer_in" ? t : sibling;
      setEditTf(t);
      setTfForm({
        date: t.date,
        from_cash_bank_id: outRow?.cash_bank_id || "",
        to_cash_bank_id: inRow?.cash_bank_id || "",
        amount: inRow?.amount || t.amount,
        admin_fee: Math.max(0, (outRow?.amount || 0) - (inRow?.amount || t.amount)),
        admin_fee_account_code: "6500",
        description: t.description || "", reference: t.reference || "",
      });
      setTfOpen(true);
    }
  };

  const [importDlg, setImportDlg] = useState(null); // {kind: "receipt"|"payment"|"transfer"}
  const [importProg, setImportProg] = useState(null); // { loading, fileName, result }
  const doImportCashBank = async (kind, file) => {
    const endpoint = kind === "receipt" ? "cashbank-receipt" : kind === "payment" ? "cashbank-payment" : "cashbank-transfer";
    const token = localStorage.getItem("nusa_token");
    const fd = new FormData(); fd.append("file", file);
    setImportDlg(null);
    setImportProg({ loading: true, fileName: file.name });
    try {
      const resp = await fetch(`${API}/bulk-import/${endpoint}`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Gagal impor");
      const errors = (data.errors || []).map((e) => (typeof e === "string" ? e : `${e.row}: ${e.error}`));
      setImportProg({ loading: false, fileName: file.name, result: { ...data, errors } });
      toast.success(`Impor selesai: ${data.created} tersimpan, ${data.skipped} dilewati${errors.length ? `, ${errors.length} gagal` : ""}`);
      load();
    } catch (e) { setImportProg(null); toast.error(e.message); }
  };
  const dlTemplate = async (kind) => {
    const endpoint = kind === "receipt" ? "cashbank-receipt" : kind === "payment" ? "cashbank-payment" : "cashbank-transfer";
    const token = localStorage.getItem("nusa_token");
    const resp = await fetch(`${API}/bulk-import/${endpoint}/template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `template_${kind}.xlsx`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const del = async (id) => { if (!window.confirm("Hapus transaksi + jurnal-nya?")) return; await api.delete(`/cashbank/transactions/${id}`); load(); };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl" data-testid="cashbank-page">
      <div className="flex flex-wrap justify-between items-baseline gap-2">
        <div>
          <h1 className="font-serif-display text-4xl font-medium text-slate-900">Kas & Bank</h1>
          <p className="text-sm text-slate-500 mt-1">Akun kas/bank + transaksi masuk, keluar, dan pindah dana. Jurnal terbuat otomatis.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setRcpOpen(true)} data-testid="new-receipt-btn"><ArrowDown size={16} className="mr-1 text-emerald-600" />Uang Masuk</Button>
          <Button variant="outline" onClick={() => setPayOpen(true)} data-testid="new-payment-btn"><ArrowUp size={16} className="mr-1 text-rose-600" />Uang Keluar</Button>
          <Button variant="outline" onClick={() => setTfOpen(true)} data-testid="new-transfer-btn"><ArrowsLeftRight size={16} className="mr-1 text-sky-600" />Pindah Dana</Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="accounts" data-testid="tab-cb-accounts">Akun Kas/Bank</TabsTrigger>
            <TabsTrigger value="txns" data-testid="tab-cb-txns">Transaksi</TabsTrigger>
          </TabsList>
          <div className="relative w-72">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari akun, deskripsi, referensi…" className="pl-8 h-9" data-testid="cashbank-search-input" />
          </div>
          <DateRangeFilter range={dateRange} onChange={setDateRange} testidPrefix="cashbank" />
        </div>

        <TabsContent value="accounts" className="mt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={accOpen} onOpenChange={setAccOpen}>
              <DialogTrigger asChild><Button className="bg-amber-600" data-testid="new-cb-account-btn" onClick={openNewAcc}><Plus size={16} className="mr-1" />Akun</Button></DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
                <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>{editAcc ? `Edit Akun & Mapping COA — ${editAcc.name}` : "Akun Kas/Bank Baru"}</DialogTitle></DialogHeader>
                <div className="space-y-3 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
                  <div><Label>Nama Akun</Label><Input value={accForm.name} onChange={(e) => setAccForm({ ...accForm, name: e.target.value })} placeholder="Bank BCA 1234567890" data-testid="cb-name-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Tipe</Label>
                      <Select value={accForm.type} onValueChange={(v) => setAccForm({ ...accForm, type: v, account_code: v === "cash" ? "1000" : "1100" })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="cash">Kas</SelectItem><SelectItem value="bank">Bank</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div><Label>Akun COA</Label>
                      <Select value={accForm.account_code} onValueChange={(v) => setAccForm({ ...accForm, account_code: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{coa.filter(a => a.type === "asset" && !a.is_parent).map(a => <SelectItem key={a.code} value={a.code}>{a.code} - {a.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  {accForm.type === "bank" && (
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Bank</Label><Input value={accForm.bank_name} onChange={(e) => setAccForm({ ...accForm, bank_name: e.target.value })} /></div>
                      <div><Label>No. Rekening</Label><Input value={accForm.account_number} onChange={(e) => setAccForm({ ...accForm, account_number: e.target.value })} /></div>
                      <div><Label>Atas Nama Rekening</Label><Input value={accForm.account_holder || ""} onChange={(e) => setAccForm({ ...accForm, account_holder: e.target.value })} placeholder="PT / Nama pemilik rekening" data-testid="cb-holder-input" /></div>
                      <div><Label>Kantor Cabang (KCP)</Label><Input value={accForm.bank_branch || ""} onChange={(e) => setAccForm({ ...accForm, bank_branch: e.target.value })} placeholder="KCP Sudirman" data-testid="cb-branch-input" /></div>
                    </div>
                  )}
                  <div><Label>Saldo Awal</Label><MoneyInput value={accForm.opening_balance} onChange={(e) => setAccForm({ ...accForm, opening_balance: e.target.value })} data-testid="acc-opening-balance-input" /></div>
                  <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 border-t border-slate-200 bg-background px-6 py-3">
                    <Button onClick={saveAcc} className="w-full bg-slate-900" data-testid="cb-save-btn">{editAcc ? "Simpan Perubahan" : "Simpan"}</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {filteredAccounts.length === 0 && <div className="col-span-full text-center py-10 text-slate-500 text-sm">{search ? "Tidak ada hasil yang cocok." : "Belum ada akun kas/bank."}</div>}
            {filteredAccounts.map((a) => (
              <div key={a.id} className="bg-white border border-slate-200 rounded-md p-5 cursor-pointer hover:border-amber-300 hover:shadow-sm transition-shadow" data-testid={`cb-account-${a.id}`} onClick={() => openDetail(a)} title="Klik untuk detail mutasi">
                <div className="flex items-start justify-between">
                  <div className="w-9 h-9 rounded bg-slate-900 text-amber-500 flex items-center justify-center">
                    {a.type === "bank" ? <Bank size={18} weight="duotone" /> : <Wallet size={18} weight="duotone" />}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100 uppercase">{a.type}</span>
                    <button onClick={(e) => { e.stopPropagation(); openEditAcc(a); }} className="p-1 text-slate-400 hover:text-sky-600" data-testid={`cb-edit-${a.id}`} title="Edit akun & mapping COA"><PencilSimple size={14} /></button>
                    <button onClick={(e) => { e.stopPropagation(); delAcc(a); }} className="p-1 text-slate-400 hover:text-rose-600" data-testid={`cb-del-${a.id}`} title="Hapus akun"><Trash size={14} /></button>
                  </div>
                </div>
                <div className="mt-3 text-sm font-medium">{a.name}</div>
                {a.bank_name && <div className="text-xs text-slate-500">{a.bank_name} · {a.account_number}</div>}
                <div className="text-xs text-slate-500 font-mono-plex mt-1">Akun: {a.account_code}</div>
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Saldo</div>
                  <div className="font-serif-display text-2xl font-semibold tabular-nums mt-1">{formatIDR(a.balance)}</div>
                  <div className="text-[11px] text-amber-600 mt-1">Klik kartu untuk detail mutasi →</div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="txns" className="mt-4">
          <div className="flex justify-end gap-2 mb-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setImportDlg({ kind: "receipt" })} data-testid="import-rcp-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><Upload size={14} className="mr-1" />Impor Uang Masuk</Button>
            <Button variant="outline" size="sm" onClick={() => setImportDlg({ kind: "payment" })} data-testid="import-pay-btn" className="border-rose-300 text-rose-700 hover:bg-rose-50"><Upload size={14} className="mr-1" />Impor Uang Keluar</Button>
            <Button variant="outline" size="sm" onClick={() => setImportDlg({ kind: "transfer" })} data-testid="import-tf-btn" className="border-sky-300 text-sky-700 hover:bg-sky-50"><Upload size={14} className="mr-1" />Impor Pindah Dana</Button>            <Button variant="outline" size="sm" onClick={async () => { try { const token = localStorage.getItem("nusa_token"); const resp = await fetch(`${API}/export/cash-in`, { headers: { Authorization: `Bearer ${token}` } }); if (!resp.ok) throw new Error("Gagal export"); const blob = await resp.blob(); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `cash-in_${new Date().toISOString().slice(0,10)}.xlsx`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 5000); toast.success("Pemasukan diekspor"); } catch (e) { toast.error(e.message); } }} data-testid="export-cash-in-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><DownloadSimple size={14} className="mr-1" />Export Pemasukan</Button>
            <Button variant="outline" size="sm" onClick={async () => { try { const token = localStorage.getItem("nusa_token"); const resp = await fetch(`${API}/export/cash-out`, { headers: { Authorization: `Bearer ${token}` } }); if (!resp.ok) throw new Error("Gagal export"); const blob = await resp.blob(); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `cash-out_${new Date().toISOString().slice(0,10)}.xlsx`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 5000); toast.success("Pengeluaran diekspor"); } catch (e) { toast.error(e.message); } }} data-testid="export-cash-out-btn" className="border-rose-300 text-rose-700 hover:bg-rose-50"><DownloadSimple size={14} className="mr-1" />Export Pengeluaran</Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-md" data-testid="cb-txns-table">
            <Table>
              <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Ref</TableHead><TableHead>Kas/Bank</TableHead><TableHead>Lawan Akun</TableHead><TableHead>Deskripsi</TableHead><TableHead>Jumlah</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredTxns.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500 text-sm">{search ? "Tidak ada hasil yang cocok." : "Belum ada transaksi."}</TableCell></TableRow> : pgTxns.paged.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{formatDate(t.date)}</TableCell>
                    <TableCell className="font-mono-plex text-xs">{t.reference}</TableCell>
                    <TableCell>{t.cash_bank_name}</TableCell>
                    <TableCell><span className="text-xs font-mono-plex">{t.counter_account_code}</span> {t.counter_account_name}</TableCell>
                    <TableCell className="text-sm">{t.description}</TableCell>
                    <TableCell className={`tabular-nums ${t.direction > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {t.direction > 0 ? "+" : "-"}{formatIDR(t.amount)}
                    </TableCell>
                    <TableCell className="flex gap-1 justify-end">
                      <button onClick={() => openEditTxn(t)} className="text-slate-400 hover:text-amber-600" title="Edit transaksi (jurnal akan dibuat ulang)" data-testid={`cb-txn-edit-${t.id}`}><PencilSimple size={14} /></button>
                      <button onClick={() => del(t.id)} className="text-slate-400 hover:text-rose-600" title="Hapus transaksi + jurnal-nya" data-testid={`cb-txn-del-${t.id}`}><Trash size={14} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {pgTxns.pager}
          </div>
        </TabsContent>
      </Tabs>

      {/* Receipt Dialog */}
      <Dialog open={rcpOpen} onOpenChange={(o) => { setRcpOpen(o); if (!o) setEditRcp(null); }}>
        <DialogContent className="max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0"><DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>Uang Masuk</DialogTitle></DialogHeader>
          <div className="space-y-3 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tanggal</Label><Input type="date" value={rcpForm.date} onChange={(e) => setRcpForm({ ...rcpForm, date: e.target.value })} /></div>
              <div><Label>Referensi</Label><Input value={rcpForm.reference} onChange={(e) => setRcpForm({ ...rcpForm, reference: e.target.value })} placeholder="opsional" /></div>
            </div>
            <div><Label>Masuk ke Akun</Label>
              <Select value={rcpForm.cash_bank_id} onValueChange={(v) => setRcpForm({ ...rcpForm, cash_bank_id: v })}>
                <SelectTrigger data-testid="rcp-account-select"><SelectValue placeholder="Pilih Kas/Bank" /></SelectTrigger>
                <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Sumber (Akun Lawan)</Label>
              <Select value={rcpForm.counter_account_code} onValueChange={(v) => setRcpForm({ ...rcpForm, counter_account_code: v })}>
                <SelectTrigger data-testid="rcp-counter-select"><SelectValue /></SelectTrigger>
                <SelectContent>{coa.filter(a => !a.is_parent).map(a => <SelectItem key={a.code} value={a.code}>{a.code} - {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Pelanggan (opsional)</Label>
              <Select value={rcpForm.partner_id} onValueChange={(v) => { const p = partners.find(x => x.id === v); setRcpForm({ ...rcpForm, partner_id: v, partner_name: p?.name || "" }); }}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{partners.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Jumlah</Label><MoneyInput value={rcpForm.amount} onChange={(e) => setRcpForm({ ...rcpForm, amount: e.target.value })} data-testid="rcp-amount-input" /></div>
            <div><Label>Proyek (opsional)</Label>
              <ProjectCombobox projects={projects} value={rcpForm.project_id || ""} testid="rcp-project-select"
                onChange={(p) => setRcpForm({ ...rcpForm, project_id: p?.id || "", project_name: p?.name || "" })} />
            </div>
            <div><Label>Deskripsi</Label><Textarea rows={3} placeholder="Tulis deskripsi lengkap, boleh kalimat panjang..." value={rcpForm.description} onChange={(e) => setRcpForm({ ...rcpForm, description: e.target.value })} data-testid="rcp-desc-input" /></div>
            <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 border-t border-slate-200 bg-background px-6 py-3">
              <Button onClick={saveRcp} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="rcp-save-btn">Simpan & Post Jurnal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={payOpen} onOpenChange={(o) => { setPayOpen(o); if (!o) setEditPay(null); }}>
        <DialogContent className="max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0"><DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>Uang Keluar</DialogTitle></DialogHeader>
          <div className="space-y-3 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tanggal</Label><Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} /></div>
              <div><Label>Referensi</Label><Input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} /></div>
            </div>
            <div><Label>Bayar dari Akun</Label>
              <Select value={payForm.cash_bank_id} onValueChange={(v) => setPayForm({ ...payForm, cash_bank_id: v })}>
                <SelectTrigger data-testid="pay-account-select"><SelectValue placeholder="Pilih Kas/Bank" /></SelectTrigger>
                <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Tujuan (Akun Lawan)</Label>
              <Select value={payForm.counter_account_code} onValueChange={(v) => setPayForm({ ...payForm, counter_account_code: v })}>
                <SelectTrigger data-testid="pay-counter-select"><SelectValue /></SelectTrigger>
                <SelectContent>{coa.filter(a => !a.is_parent).map(a => <SelectItem key={a.code} value={a.code}>{a.code} - {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Vendor (opsional)</Label>
              <Select value={payForm.partner_id} onValueChange={(v) => { const p = partners.find(x => x.id === v); setPayForm({ ...payForm, partner_id: v, partner_name: p?.name || "" }); }}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{partners.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Jumlah</Label><MoneyInput value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="pay-amount-input" /></div>
            <div><Label>Proyek (opsional)</Label>
              <ProjectCombobox projects={projects} value={payForm.project_id || ""} testid="pay-project-select"
                onChange={(p) => setPayForm({ ...payForm, project_id: p?.id || "", project_name: p?.name || "" })} />
            </div>
            <div><Label>Deskripsi</Label><Textarea rows={3} placeholder="Tulis deskripsi lengkap, boleh kalimat panjang..." value={payForm.description} onChange={(e) => setPayForm({ ...payForm, description: e.target.value })} data-testid="pay-desc-input" /></div>
            <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 border-t border-slate-200 bg-background px-6 py-3">
              <Button onClick={savePay} className="w-full bg-rose-600 hover:bg-rose-700" data-testid="pay-save-btn">Simpan & Post Jurnal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={tfOpen} onOpenChange={(o) => { setTfOpen(o); if (!o) setEditTf(null); }}>
        <DialogContent className="max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0"><DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>Pindah Dana Antar Bank</DialogTitle></DialogHeader>
          <div className="space-y-3 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
            <div><Label>Tanggal</Label><Input type="date" value={tfForm.date} onChange={(e) => setTfForm({ ...tfForm, date: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Dari</Label>
                <Select value={tfForm.from_cash_bank_id} onValueChange={(v) => setTfForm({ ...tfForm, from_cash_bank_id: v })}>
                  <SelectTrigger data-testid="tf-from-select"><SelectValue placeholder="Sumber" /></SelectTrigger>
                  <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Ke</Label>
                <Select value={tfForm.to_cash_bank_id} onValueChange={(v) => setTfForm({ ...tfForm, to_cash_bank_id: v })}>
                  <SelectTrigger data-testid="tf-to-select"><SelectValue placeholder="Tujuan" /></SelectTrigger>
                  <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Jumlah</Label><MoneyInput value={tfForm.amount} onChange={(e) => setTfForm({ ...tfForm, amount: e.target.value })} data-testid="tf-amount-input" /></div>
            <div><Label>Biaya Admin (opsional)</Label><MoneyInput value={tfForm.admin_fee} onChange={(e) => setTfForm({ ...tfForm, admin_fee: e.target.value })} data-testid="tf-admin-fee-input" /></div>
            <div><Label>Proyek (opsional)</Label>
              <ProjectCombobox projects={projects} value={tfForm.project_id || ""} testid="tf-project-select"
                onChange={(p) => setTfForm({ ...tfForm, project_id: p?.id || "", project_name: p?.name || "" })} />
            </div>
            <div><Label>Deskripsi</Label><Textarea rows={3} placeholder="Tulis deskripsi lengkap, boleh kalimat panjang..." value={tfForm.description} onChange={(e) => setTfForm({ ...tfForm, description: e.target.value })} data-testid="tf-desc-input" /></div>
            <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 border-t border-slate-200 bg-background px-6 py-3">
              <Button onClick={saveTf} className="w-full bg-sky-600 hover:bg-sky-700" data-testid="tf-save-btn">Pindahkan & Post Jurnal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Impor Excel (receipt/payment/transfer) */}
      <Dialog open={!!importDlg} onOpenChange={(o) => { if (!o) setImportDlg(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Impor {importDlg?.kind === "receipt" ? "Uang Masuk" : importDlg?.kind === "payment" ? "Uang Keluar" : "Pindah Dana"} dari Excel</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 text-slate-700">
              Unduh template Excel — panduan pengisian lengkap ada di sheet <b>Petunjuk</b> di dalam file template. Isi tiap baris sebagai satu transaksi, lalu unggah file-nya. Nama Kas/Bank, kode akun lawan, dan kode proyek harus persis sama dengan yang ada di sistem. Transaksi akan diposting dan jurnal otomatis terbentuk.
            </div>
            <Button variant="outline" size="sm" onClick={() => dlTemplate(importDlg.kind)} className="w-full" data-testid="cb-import-tmpl"><DownloadSimple size={14} className="mr-1" />Unduh Template</Button>
            <label className="block">
              <input type="file" accept=".xlsx,.xls" onChange={(e) => e.target.files?.[0] && doImportCashBank(importDlg.kind, e.target.files[0])} className="hidden" data-testid="cb-import-file" />
              <Button asChild className="w-full bg-slate-900 hover:bg-slate-800 text-white"><span><Upload size={14} className="mr-1" />Pilih & Unggah File</span></Button>
            </label>
          </div>
        </DialogContent>
      </Dialog>

      <ImportProgressDialog open={!!importProg} loading={importProg?.loading || false} fileName={importProg?.fileName} result={importProg?.result} onClose={() => setImportProg(null)} />

      {/* Dialog Detail Mutasi Akun (klik kartu akun) */}
      <Dialog open={!!detailAcc} onOpenChange={(o) => { if (!o) setDetailAcc(null); }}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3 flex flex-row items-center justify-between gap-3">
            <DialogTitle>Mutasi — {detailAcc?.name}</DialogTitle>
            <div className="flex gap-2 pr-6">
              <Button variant="outline" size="sm" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => exportMut("pdf")} disabled={!mutations} data-testid="cb-mut-export-pdf"><FilePdf size={14} className="mr-1" />PDF</Button>
              <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => exportMut("xlsx")} disabled={!mutations} data-testid="cb-mut-export-xlsx"><FileXls size={14} className="mr-1" />Excel</Button>
            </div>
          </DialogHeader>
          <div className="min-h-0 overflow-y-auto px-6 pt-4 pb-6 space-y-4" data-testid="cb-mutations">
            <div className="flex flex-wrap items-end gap-3" data-testid="cb-mut-date-filter">
              <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" value={mutRange.from} onChange={(e) => changeMutRange({ ...mutRange, from: e.target.value })} data-testid="cb-mut-date-from" /></div>
              <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" value={mutRange.to} onChange={(e) => changeMutRange({ ...mutRange, to: e.target.value })} data-testid="cb-mut-date-to" /></div>
              <Button variant="ghost" size="sm" onClick={() => changeMutRange({ from: "", to: "" })} data-testid="cb-mut-date-all">Semua</Button>
            </div>
            {!mutations ? (
              <div className="text-sm text-slate-500 py-8 text-center">Memuat mutasi…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="border border-slate-200 rounded p-3"><div className="text-[10px] uppercase text-slate-500">Saldo Awal{mutRange.from ? ` per ${formatDate(mutRange.from)}` : ""}</div><div className="tabular-nums font-semibold" data-testid="cb-mut-opening">{formatIDR(mutations.opening_balance)}</div></div>
                  <div className="border border-emerald-200 bg-emerald-50 rounded p-3"><div className="text-[10px] uppercase text-emerald-600">Total Masuk</div><div className="tabular-nums font-semibold text-emerald-700" data-testid="cb-mut-in">+{formatIDR(mutations.total_in)}</div></div>
                  <div className="border border-rose-200 bg-rose-50 rounded p-3"><div className="text-[10px] uppercase text-rose-600">Total Keluar</div><div className="tabular-nums font-semibold text-rose-700" data-testid="cb-mut-out">-{formatIDR(mutations.total_out)}</div></div>
                  <div className="border border-slate-900 bg-slate-900 rounded p-3"><div className="text-[10px] uppercase text-amber-400">Saldo Akhir</div><div className="tabular-nums font-semibold text-white" data-testid="cb-mut-closing">{formatIDR(mutations.closing_balance)}</div></div>
                </div>
                <Table>
                  <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Ref</TableHead><TableHead>Deskripsi</TableHead><TableHead>Lawan Akun</TableHead><TableHead className="text-right">Masuk</TableHead><TableHead className="text-right">Keluar</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {mutations.mutations.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500 text-sm">Belum ada mutasi pada akun ini.</TableCell></TableRow>
                    ) : mutations.mutations.map((m) => (
                      <TableRow key={m.id} data-testid={`cb-mut-${m.id}`}>
                        <TableCell>{formatDate(m.date)}</TableCell>
                        <TableCell className="font-mono-plex text-xs">{m.reference}</TableCell>
                        <TableCell className="text-sm">{m.description}{m.partner_name ? <span className="text-xs text-slate-500"> · {m.partner_name}</span> : null}</TableCell>
                        <TableCell className="text-xs"><span className="font-mono-plex">{m.counter_account_code}</span> {m.counter_account_name}</TableCell>
                        <TableCell className="text-right tabular-nums text-emerald-700">{m.direction > 0 ? formatIDR(m.amount) : ""}</TableCell>
                        <TableCell className="text-right tabular-nums text-rose-700">{m.direction < 0 ? formatIDR(m.amount) : ""}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{formatIDR(m.balance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
